/**
 * MyPolyphenolsCard.jsx — iter907 (Feb 2026)
 *
 * Consumes /api/pathway-vault/driving-conditions to get the user's top
 * active pathways, then joins with /api/polyphenols/for-pathway/{id}
 * for each to surface the specific polyphenol classes hitting the
 * user's KEGG pathways plus food-group hints.
 *
 * Educational only — data from PhenolExplorer + CTD.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FlaskConical, Sparkles, Info, Loader2, ExternalLink } from "lucide-react";
import { useAuth } from "@/App";

const COPY = {
  bg: {
    title:       "Твоите полифеноли",
    subtitle:    "Класове фитохимикали, свързани с активните ти KEGG пътища",
    empty:       "Няма активни пътища с полифенолна литература в момента.",
    for_pathway: "За пътя",
    from_foods:  "Хранителни групи",
    compounds:   "съединения",
    loading:     "Обработва данни от PhenolExplorer + CTD…",
    source:      "Източник: PhenolExplorer 3.6 ∩ CTD chem-pathways",
    tooltip:     "Всяка колона показва полифенол класове, за които CTD има публикувана литература за въздействие върху дадения KEGG път. Хранителните групи са ориентировъчни (полифенолите се съдържат в много храни).",
  },
  en: {
    title:       "Your polyphenols",
    subtitle:    "Phytochemical classes linked to your active KEGG pathways",
    empty:       "No active pathways with polyphenol literature at the moment.",
    for_pathway: "For pathway",
    from_foods:  "Food groups",
    compounds:   "compounds",
    loading:     "Joining PhenolExplorer + CTD…",
    source:      "Source: PhenolExplorer 3.6 ∩ CTD chem-pathways",
    tooltip:     "Each column lists polyphenol classes with CTD literature evidence for the given KEGG pathway. Food groups are hints (polyphenols occur in many foods).",
  },
};

const CLASS_COLORS = {
  "Flavonoids":         { bg: "bg-fuchsia-100", text: "text-fuchsia-800", dark_bg: "bg-fuchsia-500/20", dark_text: "text-fuchsia-200" },
  "Phenolic acids":     { bg: "bg-amber-100",   text: "text-amber-800",   dark_bg: "bg-amber-500/20",   dark_text: "text-amber-200"   },
  "Stilbenes":          { bg: "bg-rose-100",    text: "text-rose-800",    dark_bg: "bg-rose-500/20",    dark_text: "text-rose-200"    },
  "Lignans":            { bg: "bg-emerald-100", text: "text-emerald-800", dark_bg: "bg-emerald-500/20", dark_text: "text-emerald-200" },
  "Other polyphenols":  { bg: "bg-slate-100",   text: "text-slate-800",   dark_bg: "bg-slate-500/20",   dark_text: "text-slate-200"   },
};

const chipCls = (cls, isDark) => {
  const cfg = CLASS_COLORS[cls] || CLASS_COLORS["Other polyphenols"];
  return isDark
    ? `${cfg.dark_bg} ${cfg.dark_text} border border-slate-700/50`
    : `${cfg.bg} ${cfg.text} border border-slate-200`;
};

export default function MyPolyphenolsCard({ isDark = false, lang = "bg", testid = "my-polyphenols-card", maxPathways = 3 }) {
  const { API } = useAuth();
  const t = COPY[lang] || COPY.bg;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);  // [{ pathway_id, name_bg, class_summary: [...] }]

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const dc = await axios.get(`${API}/pathway-vault/driving-conditions`);
        const pw = (dc?.data?.pathway_ids || []).slice(0, maxPathways);
        if (cancelled) return;
        if (!pw.length) {
          setRows([]);
          setLoading(false);
          return;
        }
        const results = await Promise.all(pw.map(pid =>
          axios.get(`${API}/polyphenols/for-pathway/${encodeURIComponent(pid)}?limit=20`)
            .then(r => r.data)
            .catch(() => null),
        ));
        if (cancelled) return;
        const usable = results.filter(r => r && (r.class_summary || []).length > 0);
        setRows(usable);
      } catch (e) {
        if (!cancelled) setError(e?.message || "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, maxPathways]);

  const cardCls = isDark
    ? "border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-900/20 to-purple-900/20"
    : "border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-purple-50";

  return (
    <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid={testid}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? "bg-fuchsia-500/25 text-fuchsia-300" : "bg-fuchsia-100 text-fuchsia-700"}`}>
          <FlaskConical className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"} flex items-center gap-1`}>
            {t.title}
            <span title={t.tooltip} className="cursor-help opacity-60">
              <Info className="w-3 h-3" />
            </span>
          </div>
          <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {t.subtitle}
          </div>
        </div>
      </div>

      {loading && (
        <div className={`mt-3 flex items-center gap-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t.loading}
        </div>
      )}

      {!loading && error && (
        <div className={`mt-3 text-xs ${isDark ? "text-rose-300" : "text-rose-600"}`}>
          Load error: {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className={`mt-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {t.empty}
        </div>
      )}

      {/* Rows */}
      {!loading && !error && rows.length > 0 && (
        <div className="mt-3 space-y-3" data-testid={`${testid}-rows`}>
          {rows.map((r) => (
            <div
              key={r.pathway_id}
              className={`rounded-xl p-2.5 ${isDark ? "bg-slate-900/40" : "bg-white/70"} border ${isDark ? "border-slate-700/40" : "border-slate-200"}`}
              data-testid={`polyphenol-row-${r.pathway_id}`}
            >
              <div className={`text-[11px] font-bold flex items-center gap-1 ${isDark ? "text-slate-200" : "text-slate-900"}`}>
                <Sparkles className="w-3 h-3 opacity-70" />
                {r.name_bg}
                <span className={`text-[10px] font-normal ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  · {r.kegg}
                </span>
              </div>
              {/* Class chips */}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {r.class_summary.map((c) => (
                  <span
                    key={c.class}
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${chipCls(c.class, isDark)}`}
                    data-testid={`polyphenol-chip-${r.pathway_id}-${c.class.replace(/\s/g, "-")}`}
                  >
                    {c.class} · {c.count}
                  </span>
                ))}
              </div>
              {/* Food group hints — union across all classes on this row */}
              {(() => {
                const groups = new Set();
                r.class_summary.forEach(c => (c.food_groups || []).forEach(g => groups.add(g)));
                if (!groups.size) return null;
                return (
                  <div className={`mt-1.5 text-[10px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    <span className="opacity-70">{t.from_foods}:</span>{" "}
                    <span>{[...groups].join(" · ")}</span>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className={`mt-3 flex items-center justify-between text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
        <span>{t.source}</span>
        <a
          href="http://phenol-explorer.eu/"
          target="_blank"
          rel="noreferrer"
          className={`inline-flex items-center gap-0.5 hover:opacity-100 ${isDark ? "text-slate-300" : "text-slate-600"}`}
          data-testid={`${testid}-source-link`}
        >
          phenol-explorer.eu <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
    </div>
  );
}
